import React, { useState, useEffect, useRef } from "react";
import { Modal, Form, Alert } from "react-bootstrap";
import { useLocation } from 'react-router-dom';
import ResetTaskProgressBar from "./ResetTaskProgressBar";

import {
  getAnswerDeletionReasonOptions,
  deleteAnswers,
} from "../services/TeacherAPIClient.js";

const normaliseText = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ");

const normaliseComparableText = (value) => normaliseText(value).toLowerCase();

const DeleteAnswerModal = (props) => {
  // Extract props
  const {
    show,
    handleClose,
    taskName,
    studentName,
    assessmentName,
    handleDelete,
    answerIds,
    teacherid,
    isRetakeFullAssessment,
    isClassLevel,
    progressSteps,
    onProgressStepChange,
    openingContext,
    successContext,
    prefillReason,
    onWorkflowComplete
  } = props;

  // Ensure isClassLevel is a boolean with a proper default
  const isClassLevelBoolean = isClassLevel === true;

  const location = useLocation();
  const isStudentsResultsPage = location.pathname.includes('/studentsresults');
  const completionHandledRef = useRef(false);

  const [options, setOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState("");
  const [otherReason, setOtherReason] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [deletionReasonText, setDeletionReasonText] = useState("");

  // Store student name in local state
  const [storedStudentName, setStoredStudentName] = useState("");

  useEffect(() => {
    getAnswerDeletionReasonOptions()
      .then(options => {
        setOptions(options);
      })
      .catch(error => {
        console.error("Error getting deletion reason options:", error);
      });
  }, []);

  useEffect(() => {
    if (show) {
      completionHandledRef.current = false;
      onProgressStepChange?.(2);
    } else {
      setSelectedOption("");
      setOtherReason("");
      setSuccessMessage("");
      setShowConfirmation(false);
      setDeletionReasonText("");
    }
  }, [show, onProgressStepChange]);

  useEffect(() => {
    if (!show || !prefillReason || options.length < 1) {
      return;
    }

    const matchingOption = options.find(
      (option) =>
        normaliseComparableText(option.reason) === normaliseComparableText(prefillReason)
    );
    const otherOption = options.find(
      (option) => normaliseComparableText(option.reason) === "other"
    );

    if (matchingOption) {
      setSelectedOption(matchingOption.id);
      setOtherReason("");
    } else if (otherOption) {
      setSelectedOption(otherOption.id);
      setOtherReason(normaliseText(prefillReason));
    }
  }, [show, prefillReason, options]);

  // Effect to store studentName when it's available
  useEffect(() => {
    // When modal is shown and studentName is available, store it
    if (show && studentName) {

      setStoredStudentName(studentName);
    }
  }, [show, studentName]);

  const handleOptionChange = (e) => {

    setSelectedOption(e.target.value);
    if (e.target.value !== "Other") {
      setOtherReason("");
    }
  };

  const handleOtherReasonChange = (e) => {

    setOtherReason(e.target.value);
  };

  const handleDeleteClick = () => {


    if (!selectedOption) {
      alert("Please select a reason for deletion.");
      return;
    }
    if (
      selectedOption ===
      options.find((option) => option.reason === "Other")?.id &&
      !otherReason
    ) {
      alert("Please provide a reason for deletion.");
      return;
    }

    // Store the deletion reason to display in the confirmation modal
    const reasonText =
      selectedOption === options.find((option) => option.reason === "Other")?.id
        ? otherReason
        : options.find((option) => option.id === selectedOption)?.reason;

    setDeletionReasonText(reasonText);

    // Show the confirmation modal

    setShowConfirmation(true);
    onProgressStepChange?.(3);
  };

  // Handle canceling the confirmation
  const handleCancelConfirmation = () => {

    setShowConfirmation(false); // Hide confirmation modal
    onProgressStepChange?.(2);
  };

  const handleSuccessClose = () => {
    if (completionHandledRef.current) {
      return;
    }

    completionHandledRef.current = true;
    setSuccessMessage("");

    if (onWorkflowComplete) {
      onWorkflowComplete({ message: "Task reset complete." });
      return;
    }

    handleClose();

    if (isStudentsResultsPage) {

      window.location.reload();
    }
  };

  // Handle final confirmation of deletion
  const onDelete = async () => {


    const deletionData = {
      answerIds: answerIds,
      teacherId: teacherid,
      deletionReasonId: selectedOption,
      deletionReason: deletionReasonText,
    };



    try {
      const deleteSuccess = await deleteAnswers(deletionData);


      if (deleteSuccess) {


        const operationType = isRetakeFullAssessment ? "assessment" : "task";
        const operationTarget = isRetakeFullAssessment ? assessmentName : taskName;
        const learnerTarget = isClassLevelBoolean
          ? `Students in ${displayStudentName || "the class"}`
          : displayStudentName || "The student";
        const scanInstruction = isClassLevelBoolean
          ? `Simply scan the QR code for each student in ${displayStudentName || "the class"}.`
          : `Simply scan the QR code for ${displayStudentName || "the student"}.`;

        setSuccessMessage(
          `${operationTarget} ${operationType} has been reset now. ${learnerTarget} can now retake ${operationTarget}. ${scanInstruction}`
        );
        onProgressStepChange?.(4);

        // Reset selection states
        setSelectedOption("");
        setOtherReason("");
        setShowConfirmation(false);

        // Notify parent about successful deletion
        handleDelete(taskName, deletionReasonText, false);

        // Auto-close the entire modal after 5 seconds
        setTimeout(() => {
          handleSuccessClose();
        }, 5000);
      } else {
        console.error("Deletion failed");
      }
    } catch (error) {
      console.error("Error in deletion process:", error);
    }
  };

  // Use the stored student name if available, otherwise fall back to the prop
  const displayStudentName = storedStudentName || studentName;

  // Helper function to get appropriate button text
  const getButtonText = () => {
    if (isRetakeFullAssessment) {
      return `Retake Assessment${isClassLevelBoolean ? " for Class" : ""}`;
    } else {
      return `Retake Task${isClassLevelBoolean ? " for Class" : ""}`;
    }
  };

  // Helper function to get task count for confirmation
  const getTaskCount = () => {
    if (!answerIds || answerIds.length <= 1) return "";
    return ` (${answerIds.length} tasks)`;
  };

  const currentProgressStep = successMessage ? 4 : showConfirmation ? 3 : 2;
  const progressBar = progressSteps ? (
    <ResetTaskProgressBar
      steps={progressSteps}
      currentStep={currentProgressStep}
    />
  ) : null;
  const modalClassName = progressSteps ? "guided-reset-answer-modal" : undefined;
  const modalDialogClassName = progressSteps ? "guided-reset-answer-dialog" : undefined;

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      className={modalClassName}
      dialogClassName={modalDialogClassName}
    >
      {successMessage ? (
        // Success message view
        <>
          <Modal.Header>
            <Modal.Title>Success!</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {progressBar}
            <div className="text-center mb-3">
              <span style={{ fontSize: "48px", color: "#28a745" }}>✓</span>
            </div>
            <p className="text-center">{successMessage}</p>
            {successContext && (
              <p className="text-center text-secondary">{successContext}</p>
            )}
          </Modal.Body>
          <Modal.Footer>
            <button className="gold-action-button" onClick={handleSuccessClose}>
              Dismiss
            </button>
          </Modal.Footer>
        </>
      ) : showConfirmation ? (
        // Confirmation view
        <>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Retake</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {progressBar}
            <p>
              {isRetakeFullAssessment
                ? `Are you sure you want to retake the following assessment${isClassLevelBoolean ? " for the entire class" : ""}${getTaskCount()}?`
                : `Are you sure you want to retake the following task${isClassLevelBoolean ? " for the entire class" : ""}?`}
            </p>
            {!isRetakeFullAssessment && (
              <p>
                <strong>Task Name:</strong> {taskName}
              </p>
            )}
            <p>
              <strong>{isClassLevelBoolean ? "Class Name:" : "Student Name:"}</strong>{" "}
              {displayStudentName}
            </p>
            <p>
              <strong>Assessment Name:</strong> {assessmentName}
            </p>
            {isRetakeFullAssessment && answerIds && answerIds.length > 1 && (
              <p>
                <strong>Number of Tasks:</strong> {answerIds.length}
              </p>
            )}
            <p>
              <strong>Retake Reason:</strong> {deletionReasonText}
            </p>
          </Modal.Body>
          <Modal.Footer>
            <button
              className="white-action-button"
              onClick={handleCancelConfirmation}
            >
              Cancel
            </button>
            <button className="gold-action-button" onClick={onDelete}>
              Confirm Retake
            </button>
          </Modal.Footer>
        </>
      ) : (
        // Initial selection view
        <>
          <Modal.Header closeButton>
            <Modal.Title>
              {isRetakeFullAssessment
                ? "Retake Assessment"
                : "Retake Task"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {progressBar}
            {openingContext && (
              <Alert variant="info">{openingContext}</Alert>
            )}
            <div className="modal-info">
              {!isRetakeFullAssessment && (
                <p>
                  <strong>Task Name:</strong> {taskName}
                </p>
              )}
              <p>
                <strong>
                  {isClassLevelBoolean ? "Class Name:" : "Student Name:"}
                </strong>{" "}
                {displayStudentName}
              </p>
              <p>
                <strong>Assessment Name:</strong> {assessmentName}
              </p>
              {isRetakeFullAssessment && answerIds && answerIds.length > 1 && (
                <p>
                  <strong>Number of Tasks:</strong> {answerIds.length}
                </p>
              )}
            </div>
            <Form.Group className="mb-3" controlId="retakeReasonSelect">
              <Form.Label>
                <strong>Reason for Retake</strong>
              </Form.Label>
              <Form.Control
                as="select"
                value={selectedOption}
                onChange={handleOptionChange}
              >
                <option value="" disabled>
                  Select a reason...
                </option>
                {options.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.reason}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
            {selectedOption ===
              options.find((option) => option.reason === "Other")?.id && (
                <Form.Group className="mt-3">
                  <Form.Label>
                    <strong>Other Reason</strong>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    value={otherReason}
                    onChange={handleOtherReasonChange}
                    className="other-answer-option-width"
                  />
                </Form.Group>
              )}
          </Modal.Body>
          <Modal.Footer>
            <button className="white-action-button" onClick={handleClose}>
              Cancel
            </button>
            <button className="gold-action-button" onClick={handleDeleteClick}>
              {getButtonText()}
            </button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default DeleteAnswerModal;
